import {useIsRTL} from '@aboutyou/i18n';
import {styled} from '@linaria/react';
import React, {PropsWithChildren} from 'react';

type Position = 'top' | 'right' | 'bottom' | 'left';
type Size = number | string;

export interface Props
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  /**
   * Use disabled for hiding the fade overlay,
   * e.g. when the last item in a scrollable list ist reached.
   */
  disabled?: boolean;
  position: Position;
  /**
   * Size can be an absolute, e.g. '20px' or relative like 10%.
   * For position top and bottom size used height, for left and right it uses width.
   */
  size: Size;
  visible?: boolean;
  darkGradient?: string;
  'data-testid'?: string;
  tabIndex?: number;
}

export const FadePanel = ({
  children,
  disabled,
  position,
  size,
  visible = true,
  className,
  darkGradient,
  style,
  'data-testid': dataTestId,
  tabIndex,
  ref,
  ...restProps
}: PropsWithChildren<Props> & {ref?: React.Ref<HTMLDivElement>}) => {
  const isRTL = useIsRTL();
  return (
    <Wrapper
      disabled={disabled}
      position={position}
      size={size}
      visible={visible}
      $isRTL={isRTL}
      data-testid={dataTestId}
      ref={ref}
      className={className}
      style={style}
      $darkGradient={darkGradient}
      tabIndex={tabIndex}
      {...restProps}
    >
      {children}
    </Wrapper>
  );
};

const fadeOutGradientColor = 'rgba(255, 255, 255, 0), rgb(255, 255, 255)';
const fadeOutGradientDarkThemeColor = 'rgb(0 0 0 / 0%), rgb(0 0 0)';

const Wrapper = styled.div<{
  disabled?: boolean;
  position: Position;
  size: Size;
  visible: boolean;
  $isRTL?: boolean;
  $darkGradient?: string;
}>`
  position: relative;
  overflow: hidden;
  display: ${({visible}) => (visible ? 'inline-flex' : 'hidden')};
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;

  &::after {
    content: '';
    pointer-events: none;
    opacity: ${({visible}) => (visible ? '1' : '0')};
    position: absolute;
    top: ${({position}) => (position === 'bottom' ? 'initial' : '0')};
    right: ${({position}) => (position === 'left' ? 'initial' : '0')};
    bottom: ${({position}) => (position === 'top' ? 'initial' : '0')};
    left: ${({position}) => (position === 'right' ? 'initial' : '0')};
    display: ${({disabled}) => (disabled ? 'none' : 'block')};

    background: ${({$isRTL, position}) => {
      switch (position) {
        case 'top':
          return `linear-gradient(0, ${fadeOutGradientColor})`;
        case 'right':
          return `linear-gradient(${
            $isRTL ? -90 : 90
          }deg, ${fadeOutGradientColor})`;
        case 'bottom':
          return `linear-gradient(180deg, ${fadeOutGradientColor})`;
        case 'left':
          return `linear-gradient(${
            $isRTL ? -270 : 270
          }deg, ${fadeOutGradientColor})`;
      }
    }};

    [data-theme='dark'] & {
      background: ${({$isRTL, position, $darkGradient}) => {
        switch (position) {
          case 'top':
            return `linear-gradient(0, ${
              $darkGradient || fadeOutGradientDarkThemeColor
            })`;
          case 'right':
            return `linear-gradient(${$isRTL ? -90 : 90}deg, ${
              $darkGradient || fadeOutGradientDarkThemeColor
            })`;
          case 'bottom':
            return `linear-gradient(180deg, ${
              $darkGradient || fadeOutGradientDarkThemeColor
            })`;
          case 'left':
            return `linear-gradient(${$isRTL ? -270 : 270}deg, ${
              $darkGradient || fadeOutGradientDarkThemeColor
            })`;
        }
      }};
    }

    height: ${({position, size}) => {
      switch (position) {
        case 'left':
        case 'right':
          return '110%';
        case 'top':
        case 'bottom':
          return `${size}`;
      }
    }};
    width: ${({position, size}) => {
      switch (position) {
        case 'left':
        case 'right':
          return `${size}`;
        case 'top':
        case 'bottom':
          return '110%';
      }
    }};
    transition: opacity 0.25s;
  }
`;
